import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { Router } from '@angular/router';
import {PersonLogin, RoleName} from '../person/person-login';
import {SelectionItem} from '../selections/selection-item';
import {PersonLoginService} from '../services/person-login-service';
import {ProductService} from '../services/product-service';
import {SelectionService} from '../services/selection-service';
import {DateUtils} from '../utils/date-utils';
import {StringUtils} from '../utils/string-utils';
import {Product} from './product';
import {ProductPricesComponent} from './product-prices.component';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import { DatePipe, DecimalPipe } from '@angular/common';
import {BrandID} from "../enums/brand-id";

@Component({
    selector: 'product-detail',
    templateUrl: './product-detail.component.html',
    styleUrls: ['./product-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButton, MatIcon, MatDatepickerInput, MatDatepickerToggle, MatDatepicker, MatProgressSpinner, ProductPricesComponent, DecimalPipe, DatePipe]
})
export class ProductDetailComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly productService = inject(ProductService);
  private readonly router = inject(Router);
  private readonly selectionService = inject(SelectionService);
  readonly brandid = input<string>(undefined!);
  readonly productId = input.required<string>({ alias: 'product-id' });


  public personLogin!: PersonLogin;
  public readonly loadedProduct = signal<Product>(undefined!);
  public readonly isNew = signal<boolean>(false);
  public readonly productTypes = signal<SelectionItem[]>(undefined!);
  public readonly commodities = signal<SelectionItem[]>(undefined!);
  public readonly productLines = signal<SelectionItem[]>(undefined!);
  public readonly productClassCodes = signal<SelectionItem[]>(undefined!);
  public readonly productModels = signal<SelectionItem[]>(undefined!);
  public readonly productLeadTimes = signal<SelectionItem[]>(undefined!);
  public readonly uomCodes = signal<SelectionItem[]>(undefined!);
  public pricesVisible: boolean = false;
  public readonly isBusy = signal<boolean>(false);
  public readonly errorMessage = signal<string>(undefined!);
  brandIDs: string[] = Object.keys(BrandID);

  public brandChange() {
    this.selectionService.getProductModels(this.loadedProduct().brandID).subscribe(result => this.productModels.set(result));
    this.selectionService.getProductLines(this.loadedProduct().brandID).subscribe(result => this.productLines.set(result));
  }

  public showPrices() {
    this.pricesVisible = true;
  }

  public updateWhereUsed(newValue: any): void {
    if (isNaN(+newValue)) {
      this.loadedProduct().unitSize = 0;
    } else {
      this.loadedProduct().unitSize = +newValue;
    }
  }

  public formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  public setBeginEffective(newValue: Date) {
    this.loadedProduct().beginEffectiveDate = DateUtils.formatOutput(newValue);
  }

  public setEndEffective(newValue: Date) {
    this.loadedProduct().endEffectiveDate = DateUtils.formatOutput(newValue);
  }

  private initialize(): void {
    this.isNew.set(false);
    this.loadSelectionLists();
    this.initializeFromRouteParams();
  }

  public backToProductSearch(): void {
    this.router.navigate(['product']);
  }

  private loadSelectionLists(): void {
    this.selectionService.getProductTypes().subscribe(result => this.productTypes.set(result));
    this.selectionService.getCommodities().subscribe(result => this.commodities.set(result));
    this.selectionService.getProductClassCodes().subscribe(result => this.productClassCodes.set(result));
    this.selectionService.getProductLeadTimes().subscribe(result => this.productLeadTimes.set(result));
    this.selectionService.getUomCodes().subscribe(result => this.uomCodes.set(result));
  }

  private startNewProduct(): void {
    this.isNew.set(true);
    this.loadedProduct.set(new Product());
    this.loadedProduct().active = true;
    this.loadedProduct().unitSize = 0;
    this.loadedProduct().beginEffectiveDate = new Date(2001, 0, 1);
    this.loadedProduct().endEffectiveDate = new Date(2029, 11, 31);
    this.loadedProduct().createTimestamp = new Date();
    this.loadedProduct().createUser = this.personLogin.person.userLogin;
    this.loadedProduct().updateTimestamp = new Date();
    this.loadedProduct().updateUser = this.personLogin.person.userLogin;
  }

  public saveProduct(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
    this.loadedProduct().updateUser = this.personLogin.person.userLogin;
    this.productService.save(this.loadedProduct()).subscribe(() => {
      this.isBusy.set(false);
      this.productService.loadProduct(this.loadedProduct().productId, this.loadedProduct().brandID).subscribe(result => this.loadedProduct.set(result));
      this.loadedProduct.set(null!);
    }, error => this.handleError(error));
  }

  private handleError(error: any) {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnInit(): void {
    if (!this.personLoginService.hasRole(RoleName.IT)) {
      this.router.navigate(['main']);
    }
    this.personLogin = this.personLoginService.getCachedLogin()!;
    this.initialize();
  }

  viewSkuSlots(): void {
    this.router.navigate(['productSkuSlots', this.loadedProduct().productId]);
  }


  private initializeFromRouteParams(): void {
    let idString: string = this.productId();
    if (idString && idString.length > 0) {
      if ("new" === idString) {
        this.startNewProduct();
      } else {
        let brandID = BrandID[this.brandid() as keyof typeof BrandID];
        this.productService.loadProduct(idString, brandID).subscribe(result => {
          this.loadedProduct.set(result);
          this.brandChange();
        });
      }
    } else {
      this.backToProductSearch();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initializeFromRouteParams();
    }
  }
}

